import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Wheat, Sparkles, Paperclip, FileText, Beef, Plus, MessageSquare, Trash2, ChevronLeft } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useProductStore } from '../../store/productStore';
import { chatbotApi, chatThreadApi } from '../../services/api';
import DevelopmentNotice from './DevelopmentNotice';

interface Message {
  id: number;
  text: string;
  sender: 'bot' | 'user';
}

interface ChatThreadItem {
  id: string;
  title: string;
  product: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

// V2 preview: the assistant is shown but not yet released. Flip to false to
// re-enable live chat when Version 2 ships.
const UNDER_DEVELOPMENT = true;

function formatBotText(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-bold text-[#0f206c]">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

const BOT_CONFIG = {
  grain: {
    name: 'Bushel View Bot',
    subtitle: 'AI Grain Marketing Assistant',
    welcome: "Hi! I'm **Bushel View Bot**, your AI grain marketing assistant. How can I help you today?",
    placeholder: 'Ask about your grain marketing...',
    icon: Wheat,
    iconColor: 'text-[#d4a843]',
  },
  beef: {
    name: 'Beef View Bot',
    subtitle: 'AI Cattle & LRP Assistant',
    welcome: "Hi! I'm **Beef View Bot**, your AI cattle marketing and LRP insurance assistant. How can I help you today?",
    placeholder: 'Ask about your LRP coverage...',
    icon: Beef,
    iconColor: 'text-orange-400',
  },
} as const;

export default function ChatBot() {
  const { activeProduct } = useProductStore();
  const config = BOT_CONFIG[activeProduct];
  const BotIcon = config.icon;

  const [isOpen, setIsOpen] = useState(false);
  const [showThreads, setShowThreads] = useState(false);
  const [threads, setThreads] = useState<ChatThreadItem[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([
    { id: 0, text: config.welcome, sender: 'bot' },
  ]);
  const [conversationHistory, setConversationHistory] = useState<
    { role: 'user' | 'assistant'; content: string }[]
  >([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [docCount, setDocCount] = useState<number | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const nextId = useRef(1);
  const abortControllerRef = useRef<AbortController | null>(null);

  const user = useAuthStore((s) => s.user);
  const canUpload = user?.role === 'admin' || user?.role === 'manager';

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && !showThreads && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, showThreads]);

  // Bounce animation
  useEffect(() => {
    const timer = setTimeout(() => setHasAnimated(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Load threads + doc count when panel opens
  useEffect(() => {
    if (isOpen) {
      chatbotApi.stats().then((res) => setDocCount(res.data.total_chunks)).catch(() => setDocCount(null));
      loadThreads();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadThreads = useCallback(async () => {
    try {
      const res = await chatThreadApi.list();
      setThreads(res.data.threads);
    } catch {
      // Threads not available
    }
  }, []);

  const startNewChat = () => {
    setActiveThreadId(null);
    setMessages([{ id: 0, text: config.welcome, sender: 'bot' }]);
    setConversationHistory([]);
    nextId.current = 1;
    setShowThreads(false);
  };

  const loadThread = async (threadId: string) => {
    try {
      const res = await chatThreadApi.get(threadId);
      const thread = res.data;
      setActiveThreadId(thread.id);

      // Convert stored messages to display format
      const displayMessages: Message[] = [{ id: 0, text: config.welcome, sender: 'bot' }];
      const history: { role: 'user' | 'assistant'; content: string }[] = [];
      let id = 1;

      for (const msg of thread.messages) {
        displayMessages.push({
          id: id++,
          text: msg.content,
          sender: msg.role === 'user' ? 'user' : 'bot',
        });
        history.push({ role: msg.role as 'user' | 'assistant', content: msg.content });
      }

      setMessages(displayMessages);
      setConversationHistory(history);
      nextId.current = id;
      setShowThreads(false);
    } catch {
      // Failed to load thread
    }
  };

  const deleteThread = async (threadId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await chatThreadApi.delete(threadId);
      setThreads((prev) => prev.filter((t) => t.id !== threadId));
      if (activeThreadId === threadId) {
        startNewChat();
      }
    } catch {
      // Failed to delete
    }
  };

  const saveMessagesToThread = async (
    threadId: string,
    userText: string,
    botText: string
  ) => {
    try {
      await chatThreadApi.addMessages(threadId, [
        { role: 'user', content: userText },
        { role: 'assistant', content: botText },
      ]);
      loadThreads();
    } catch {
      // Silent fail
    }
  };

  const streamResponse = useCallback(
    async (userText: string, history: { role: 'user' | 'assistant'; content: string }[]) => {
      const newHistory = [...history, { role: 'user' as const, content: userText }];

      const botMsgId = nextId.current++;
      setMessages((prev) => [...prev, { id: botMsgId, text: '', sender: 'bot' }]);
      setIsTyping(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      let fullText = '';
      let currentThreadId = activeThreadId;

      // Auto-create thread on first message if no active thread
      if (!currentThreadId) {
        try {
          const res = await chatThreadApi.create({
            title: userText.slice(0, 80) + (userText.length > 80 ? '...' : ''),
            product: activeProduct,
          });
          currentThreadId = res.data.id;
          setActiveThreadId(currentThreadId);
        } catch {
          // Continue without persistence
        }
      }

      try {
        const response = await fetch(`${API_BASE}/chatbot/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ messages: newHistory, product: activeProduct }),
          signal: controller.signal,
        });

        if (response.status === 503) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === botMsgId
                ? { ...m, text: 'Chatbot service is not configured. Please contact your administrator.' }
                : m
            )
          );
          setIsTyping(false);
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const jsonStr = line.slice(6).trim();
            if (!jsonStr) continue;

            try {
              const event = JSON.parse(jsonStr);
              if (event.type === 'content') {
                fullText += event.text;
                setMessages((prev) =>
                  prev.map((m) => (m.id === botMsgId ? { ...m, text: fullText } : m))
                );
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (!fullText) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === botMsgId
                ? { ...m, text: 'Sorry, something went wrong. Please try again.' }
                : m
            )
          );
        }
      } finally {
        setIsTyping(false);
        abortControllerRef.current = null;
      }

      if (fullText) {
        setConversationHistory([...newHistory, { role: 'assistant', content: fullText }]);
        // Persist to thread
        if (currentThreadId) {
          saveMessagesToThread(currentThreadId, userText, fullText);
        }
      }
    },
    [activeProduct, activeThreadId] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const sendMessage = (text: string) => {
    if (UNDER_DEVELOPMENT) return;
    if (!text.trim() || isTyping) return;
    const userMsg: Message = { id: nextId.current++, text: text.trim(), sender: 'user' };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    streamResponse(text.trim(), conversationHistory);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadStatus('Uploading...');
    try {
      const res = await chatbotApi.upload(file);
      setUploadStatus(`Ingested ${res.data.chunks} chunks from ${file.name}`);
      setDocCount(res.data.total_documents);
    } catch {
      setUploadStatus('Upload failed. Please try again.');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    setTimeout(() => setUploadStatus(null), 4000);
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className={`fixed bottom-20 right-6 z-40 w-12 h-12 ${activeProduct === 'beef' ? 'bg-orange-800' : 'bg-[#0f206c]'} text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center ${
            !hasAnimated ? 'animate-bounce-gentle' : ''
          }`}
          title={config.name}
        >
          <BotIcon className={`w-6 h-6 ${config.iconColor}`} />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-40 w-full sm:w-[420px] h-[100dvh] sm:h-[540px] flex flex-col bg-white sm:rounded-2xl shadow-2xl overflow-hidden animate-scale-in border border-gray-200">
          {/* Header */}
          <div className={`bg-gradient-to-r ${activeProduct === 'beef' ? 'from-orange-800 to-orange-700' : 'from-[#0f206c] to-[#1a3a8c]'} px-4 py-3.5 flex items-center justify-between flex-shrink-0`}>
            <div className="flex items-center gap-3 min-w-0">
              {showThreads ? (
                <button
                  onClick={() => setShowThreads(false)}
                  className="p-1 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              ) : (
                <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                  <BotIcon className={`w-5 h-5 ${config.iconColor}`} />
                </div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-white font-semibold text-sm">
                    {showThreads ? 'Chat History' : config.name}
                  </h3>
                  {!showThreads && <Sparkles className="w-3.5 h-3.5 text-[#d4a843]" />}
                </div>
                <p className="text-blue-200 text-[11px]">
                  {showThreads
                    ? `${threads.length} conversation${threads.length !== 1 ? 's' : ''}`
                    : config.subtitle}
                  {!showThreads && docCount !== null && docCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center gap-0.5">
                      <FileText className="w-2.5 h-2.5" />
                      {docCount}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {!showThreads && (
                <>
                  <button
                    onClick={() => setShowThreads(true)}
                    className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    title="Chat history"
                  >
                    <MessageSquare className="w-4 h-4" />
                  </button>
                  <button
                    onClick={startNewChat}
                    className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    title="New chat"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </>
              )}
              {canUpload && !showThreads && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.txt,.md"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    title="Upload document for context"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                </>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Upload Status */}
          {uploadStatus && !showThreads && (
            <div className="px-4 py-2 bg-blue-50 text-blue-700 text-xs border-b border-blue-100">
              {uploadStatus}
            </div>
          )}

          {/* Thread List View */}
          {showThreads ? (
            <div className="flex-1 overflow-y-auto">
              {/* New Chat Button */}
              <button
                onClick={startNewChat}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-[#0f206c] hover:bg-blue-50 border-b border-gray-100 transition-colors"
              >
                <Plus className="w-4 h-4" />
                New Chat
              </button>

              {threads.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-400 text-sm">
                  No previous conversations
                </div>
              ) : (
                threads.map((thread) => (
                  <button
                    key={thread.id}
                    onClick={() => loadThread(thread.id)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-50 transition-colors group ${
                      activeThreadId === thread.id ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{thread.title}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {thread.message_count} messages
                        {' - '}
                        {new Date(thread.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={(e) => deleteThread(thread.id, e)}
                      className="p-1 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                      title="Delete thread"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </button>
                ))
              )}
            </div>
          ) : (
            <>
              {UNDER_DEVELOPMENT && <DevelopmentNotice compact className="mx-3 mt-3" />}
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50/50">
                {messages.map((msg, index) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in-up`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div
                      className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        msg.sender === 'user'
                          ? 'bg-harvest-50 border border-harvest-200 text-gray-800 rounded-br-md'
                          : 'bg-white border border-gray-200 text-gray-700 rounded-bl-md shadow-sm'
                      }`}
                    >
                      {msg.sender === 'bot' ? formatBotText(msg.text) : msg.text}
                    </div>
                  </div>
                ))}

                {/* Typing Indicator */}
                {isTyping && messages[messages.length - 1]?.text === '' && (
                  <div className="flex justify-start animate-fade-in-up">
                    <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce-dot" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce-dot" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce-dot" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSubmit} className="flex-shrink-0 px-3 py-3 bg-white border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={UNDER_DEVELOPMENT ? 'Chat is coming in Version 2' : config.placeholder}
                    disabled={isTyping || UNDER_DEVELOPMENT}
                    className="flex-1 px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0f206c]/20 focus:border-[#0f206c]/40 placeholder-gray-400 disabled:opacity-50 transition-all"
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || isTyping || UNDER_DEVELOPMENT}
                    className="p-2.5 bg-[#d4a843] text-white rounded-xl hover:bg-[#c09935] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 flex-shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 text-center mt-2">
                  Powered by Bushel View AI Engine
                </p>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
